import { errorWords } from "@alm/domain";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { AssignmentProgress } from "./-components/assignment-progress";
import { OutstandingPeople } from "./-components/outstanding-people";
import { RecentCompletions } from "./-components/recent-completions";

export const Route = createFileRoute("/_authenticated/_workforce/training/dashboard")({
  component: TrainingDashboard,
});

/** Where the building stands on training: what is late, what has not been started, how much of what
 * was assigned is done, and who finished something lately. It is the screen an administrator opens
 * to find out whether anything needs doing today. */
function TrainingDashboard() {
  const { facilityId } = useFacility();
  const canManage = useCan("training:manage");
  const summary = useQuery(orpc.workforce.training.summary.queryOptions({ input: { facilityId } }));
  const assignments = useQuery(
    orpc.workforce.training.listAssignments.queryOptions({ input: { facilityId } }),
  );

  /* Chasing the people who owe training: a notification each, and an email
     where there is an address. */
  const remind = useMutation(
    orpc.workforce.employeeDocuments.employees.remindTraining.mutationOptions({
      onSuccess: (result) =>
        result.reminded === 0
          ? toast.info("Nobody is outstanding")
          : toast.success(
              `Reminded ${result.reminded} ${result.reminded === 1 ? "person" : "people"}${
                result.emailed > 0 ? `, ${result.emailed} by email` : ", none by email"
              }`,
            ),
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );

  const counts = summary.data;
  const rows = assignments.data ?? [];
  const failed = rows.filter((row) => row.state === "failed").length;

  const meta = summary.isError
    ? "These numbers could not be loaded"
    : summary.isPending
      ? "Loading…"
      : `${counts?.total ?? 0} assignments, ${counts?.overdue ?? 0} overdue`;

  return (
    <>
      <PageHeader
        title="Training overview"
        meta={meta}
        actions={
          canManage && (counts?.overdue ?? 0) + (counts?.notStarted ?? 0) > 0 ? (
            <Button
              variant="outline"
              disabled={remind.isPending}
              onClick={() => remind.mutate({ facilityId })}
            >
              {remind.isPending ? "Reminding…" : "Remind everyone outstanding"}
            </Button>
          ) : null
        }
      />

      {summary.isError ? (
        <Card>
          <CardContent>
            <ReadFailed
              message="These numbers could not be loaded. This is not a statement that everybody is up to date."
              error={summary.error}
              onRetry={() => void summary.refetch()}
              retrying={summary.isFetching}
            />
          </CardContent>
        </Card>
      ) : summary.isPending || !counts ? (
        <Skeleton className="h-36 w-full rounded-xl" />
      ) : (
        <AssignmentProgress counts={counts} failed={failed} />
      )}

      <OutstandingPeople />

      <section className="space-y-3">
        <h2 className="text-lg font-semibold tracking-tight">Recently completed</h2>
        {assignments.isError ? (
          <ReadFailed
            message="Could not load recent completions."
            error={assignments.error}
            onRetry={() => void assignments.refetch()}
            retrying={assignments.isFetching}
          />
        ) : (
          <RecentCompletions rows={rows} pending={assignments.isPending} />
        )}
      </section>
    </>
  );
}
